import type { PlanAddOn } from "@circle-react/types";
import { useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";
import { communityPlanAddonApi } from "@/react/api/communityPlanAddonApi";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

const SPACES_ADDON_SLUG = "spaces";
const i18nRoot = "spaces.spaces_limit_addon_modal";

export const useSpacesLimitAddonState = (onHide: () => void) => {
  const toast = useToast();
  const [addon, setAddon] = useState<PlanAddOn | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(
    () =>
      void communityPlanAddonApi
        .index()
        .then(addons => {
          setAddon(addons.find(a => a.slug === SPACES_ADDON_SLUG) ?? null);
        })
        .catch(() => {
          toast.error(t("something_went_wrong"));
        })
        .finally(() => {
          setIsLoading(false);
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount; toast is an unstable reference
    [],
  );

  const handleConfirm = async () => {
    if (!addon) return;
    setIsSubmitting(true);
    try {
      const newQuantity = (addon.quantity as number) + addon.unit_size;
      await communityPlanAddonApi.create({
        [addon.id.toString()]: newQuantity,
      });
      toast.success(t(`${i18nRoot}.success`));
      onHide();
    } catch {
      toast.error(t("something_went_wrong"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildDescription = () => {
    if (!addon) return "";
    const key =
      addon.interval === "year"
        ? `${i18nRoot}.description_yearly`
        : `${i18nRoot}.description`;
    return t(key, {
      count: addon.unit_size,
      price: addon.formatted_base_monthly_amount,
    });
  };

  return {
    addon,
    isLoading,
    isSubmitting,
    handleConfirm,
    buildDescription,
    i18nRoot,
  };
};
